/**
 * Status badges — the one place status → colour + label is decided.
 *
 * Colour language (docs/06 §7), consistent across all three SPAs:
 *   gray = draft/pending · green = active/completed · amber = awaiting action · red = cancelled.
 * Colour is never the only signal (a11y §12): every badge carries its label as text.
 */
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui';
import type { OrderStatus, ProductStatus } from '@/shared/contract';

export function ProductStatusBadge({ status }: { status: ProductStatus }) {
  const { t } = useTranslation();

  const tone = {
    draft: 'neutral',
    active: 'green',
    archived: 'muted',
  }[status] as 'neutral' | 'green' | 'muted';

  return <Badge tone={tone}>{t(`status.${status}`)}</Badge>;
}

/**
 * The derived "awaiting MOST pricing" chip: an ACTIVE product that the admin has not priced yet
 * (`is_priced === false`) is not in the buyer catalog — the deliberate gate (docs/04 §1.2).
 *
 * This chip is the ONLY thing a producer ever learns about pricing. It says THAT the product is
 * published, never FOR HOW MUCH. Do not enrich it with a price, a range, or a margin hint: the
 * producer knows their own base price, so any marked-up figure hands them Merdan's margin by
 * subtraction (docs/04 §5).
 */
export function PricingChip({ status, isPriced }: { status: ProductStatus; isPriced: boolean }) {
  const { t } = useTranslation();

  // Only meaningful for active products: a draft is not in the pricing queue at all.
  if (status !== 'active') return null;

  return isPriced ? (
    // Green = "done / visible in the catalog" — the docs/06 §7 colour language has no blue.
    <Badge tone="green">{t('products.isPricedYes')}</Badge>
  ) : (
    <Badge tone="amber">{t('products.isPricedNo')}</Badge>
  );
}

/**
 * Shared order status. A producer may see it on their own slice — it reveals no buyer identity,
 * so it is not a leak (docs/04 §5). It is READ-ONLY here: only the admin moves an order.
 */
export function OrderStatusBadge({ status }: { status: OrderStatus }) {
  const { t } = useTranslation();

  // Same mapping as the admin's OrderStatusBadge: `new` is amber because it is awaiting
  // Merdan's action; `in_progress` is the quiet one. The two cabinets must not disagree on
  // what a colour means (docs/06 §7).
  const tone = {
    new: 'amber',
    in_progress: 'neutral',
    completed: 'green',
    cancelled: 'red',
  }[status] as 'amber' | 'neutral' | 'green' | 'red';

  return <Badge tone={tone}>{t(`status.order.${status}`)}</Badge>;
}
